import React, { useState, useEffect, useRef } from 'react'
import styles from './styles'
import { withStyles, useMediaQuery, useTheme, Box } from '@material-ui/core'
import PurposeSelectionField from './purpose-selection-field'
import OnboardingInformation from './onboarding-information'
import PurposeSelectionFieldMobile from './purpose-selection-field-mobile'
import DeliveryMethodPage from './delivery-method-page'
import { marketingGetPrimaryInterestsAction } from 'src/reducers/marketing/action'
import { useDispatch } from 'react-redux'
import { meService } from 'src/api/authentication'
import { registerMarketingInfo } from '../../api/sign-up'
import { track } from '@amplitude/analytics-browser'
import { get as getCookie, set as setCookie } from 'es-cookie'
import moment from 'moment-timezone'
import { hasDeliveryStep } from './interests'

const Purpose = props => {
  const { classes } = props
  const dispatch = useDispatch()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [pageView, setPageView] = useState(1)
  const [name, setName] = useState('')
  const [selectedInterest, setSelectedInterest] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [showDeliveryStep, setShowDeliveryStep] = useState(false)
  const [selectedDelivery, setSelectedDelivery] = useState('')
  const [purposeText, setPurposeText] = useState('')
  const rootRef = useRef(null)
  useEffect(() => {
    if (rootRef.current) {
      rootRef.current.scrollTop = 0
    }
    window.scrollTo(0, 0)
  }, [pageView, showDeliveryStep])
  const handlePageView = () => {
    setPageView(prev => prev + 1)
  }
  const handleSelectInterest = (interest, id) => {
    setSelectedInterest(interest)
    setSelectedIndex(id)
  }
  const handleInterestContinue = () => {
    if (hasDeliveryStep(selectedInterest)) {
      setShowDeliveryStep(true)
    } else {
      handlePageView()
    }
  }
  const handleDeliveryContinue = () => {
    setShowDeliveryStep(false)
    handlePageView()
  }
  const handleOnboardingComplete = async () => {
    let timeZone = 'None'
    if (moment.tz.guess() != undefined) {
      timeZone = moment.tz.guess()
    }
    const data = {
      selectedIndexofText: selectedIndex,
      purpose: purposeText,
      timeZone,
    }
    track('onboarding_complete_event', {
      purpose_text: purposeText,
      interest: selectedInterest,
      delivery_method: selectedDelivery || '',
    })
    let response = await registerMarketingInfo(data)
    if (response.statusCode == 200) {
      window.Intercom('update', {
        api_base: 'https://api-iam.intercom.io',
        app_id: 'a1mmx6vo',
        purpose_text: purposeText,
        purpose_selection: selectedInterest,
        delivery_method: selectedDelivery || '',
        hide_default_launcher: true,
      })
      await new Promise(resolve => setTimeout(resolve, 1000))
      setCookie('email', '')
      if (window.CONSTANTS?.IS_TRIAL_EXPIRED) {
        window.location.href = '/trial-expired'
        return
      }
      if (
        getCookie('redirectUrl') != '' &&
        getCookie('redirectUrl') != null &&
        getCookie('redirectUrl') != undefined
      ) {
        window.location.href = getCookie('redirectUrl')
        setCookie('redirectUrl', '')
      } else {
        // After signup: the home page.
        window.location.href = '/'
      }
    }
  }
  useEffect(() => {
    dispatch(marketingGetPrimaryInterestsAction())
    meService().then(res => {
      setName(res.data.name)
      if (res.data.trialExpired && !getCookie('email')) {
        window.location.href = '/trial-expired'
      }
    })

    // Hide Intercom launcher on purpose page
    if (window.Intercom) {
      window.Intercom('update', {
        hide_default_launcher: true,
      })
    }
  }, [dispatch])

  const InterestSelection = isMobile
    ? PurposeSelectionFieldMobile
    : PurposeSelectionField
  const PurposeForm = isMobile
    ? PurposeSelectionFieldMobile
    : PurposeSelectionField

  return (
    <div
      ref={rootRef}
      component="main"
      className={classes.root}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '30%',
          left: '10%',
          width: '400px',
          height: '400px',
          background: 'rgba(180, 225, 255, 0.5)',
          filter: 'blur(150px)',
          borderRadius: '9999px',
          zIndex: 0,
        }}
      />
      {pageView == 1 && !showDeliveryStep ? (
        <InterestSelection
          key="interest"
          mode="interest"
          onContinue={handleInterestContinue}
          onSelectInterest={handleSelectInterest}
          selectedInterest={selectedInterest}
          selectedIndex={selectedIndex}
        />
      ) : pageView == 1 && showDeliveryStep ? (
        <DeliveryMethodPage
          key="delivery"
          interest={selectedInterest}
          onContinue={handleDeliveryContinue}
          onBack={() => setShowDeliveryStep(false)}
          onSelectDelivery={setSelectedDelivery}
          selectedDelivery={selectedDelivery}
        />
      ) : pageView == 2 && (!hasDeliveryStep(selectedInterest) || !selectedDelivery) ? (
        <PurposeForm
          key="purpose"
          mode="purpose"
          selectedInterest={selectedInterest}
          selectedIndex={selectedIndex}
          onBack={() => {
            if (hasDeliveryStep(selectedInterest)) {
              setShowDeliveryStep(true)
              setPageView(1)
            } else {
              setPageView(1)
            }
          }}
          onContinue={(text) => {
            setPurposeText(text || '')
            handlePageView()
          }}
        />
      ) : (pageView == 2 && hasDeliveryStep(selectedInterest) && selectedDelivery) ||
        // Upper bound must cover the longest deck. The generic deck is 6 cards and
        // starts at pageView 3 when Q2 was skipped, so its last slide is pageView 8.
        // Leaving this at 7 renders a blank screen on the final slide.
        (pageView >= 3 && pageView <= 8) ? (
        <OnboardingInformation
          pageView={pageView}
          setPageView={setPageView}
          handlePageView={handlePageView}
          onBack={() => {
            if (hasDeliveryStep(selectedInterest) && selectedDelivery) {
              setShowDeliveryStep(true)
              setPageView(1)
            } else {
              setPageView(2)
            }
          }}
          subtitle={selectedDelivery || selectedInterest}
          selectedInterest={selectedInterest}
          // True when the user answered Q2, which is the case where this step renders
          // at pageView 2 instead of 3. The slide index is derived from it, so it must
          // match the routing condition below exactly.
          answeredDeliveryStep={
            hasDeliveryStep(selectedInterest) && !!selectedDelivery
          }
          onComplete={handleOnboardingComplete}
          name={name}
        />
      ) : null}
    </div>
  )
}

export default withStyles(styles)(Purpose)
